/**
 * Thin named drum-kit bank (separate from synth NamedPreset).
 */
import {
  cloneDrumKit,
  createDefaultDrumKit,
  createGmDrumKit,
  normalizeDrumKit,
  type DrumKit,
} from "../../../../src/lib/audio";

export type NamedDrumKit = {
  id: string;
  name: string;
  kit: DrumKit;
  builtin?: boolean;
};

export const FACTORY_STUDIO_KIT_ID = "factory-studio-kit";
export const FACTORY_GM_KIT_ID = "factory-gm-kit";

export function createDefaultDrumBank(): NamedDrumKit[] {
  const studio = createDefaultDrumKit();
  const gm = createGmDrumKit();
  return [
    {
      id: FACTORY_STUDIO_KIT_ID,
      name: studio.name,
      kit: studio,
      builtin: true,
    },
    {
      id: FACTORY_GM_KIT_ID,
      name: gm.name,
      kit: gm,
      builtin: true,
    },
  ];
}

export function isNamedDrumKit(value: unknown): value is NamedDrumKit {
  if (!value || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return typeof obj.id === "string"
    && typeof obj.name === "string"
    && obj.kit != null
    && typeof obj.kit === "object";
}

export function sanitizeDrumBank(raw: unknown): NamedDrumKit[] {
  const factory = createDefaultDrumBank();
  if (!Array.isArray(raw)) {
    return factory;
  }
  const out: NamedDrumKit[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (!isNamedDrumKit(entry)) {
      continue;
    }
    const id = entry.id.trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    const name = entry.name.trim().slice(0, 48) || "Kit";
    const isBuiltin =
      entry.builtin === true
      || id === FACTORY_STUDIO_KIT_ID
      || id === FACTORY_GM_KIT_ID;
    out.push({
      id,
      name,
      kit: normalizeDrumKit({ ...entry.kit, name }),
      builtin: isBuiltin,
    });
  }
  // Always keep factory kits available (prepend any that were omitted, factory order).
  const missing = factory.filter(builtin => !out.some(e => e.id === builtin.id));
  if (missing.length > 0) {
    out.unshift(...missing);
  }
  return out.length > 0 ? out : factory;
}

export function cloneNamedDrumKit(entry: NamedDrumKit): NamedDrumKit {
  return {
    id: entry.id,
    name: entry.name,
    kit: cloneDrumKit(entry.kit),
    ...(entry.builtin ? { builtin: true } : {}),
  };
}

/**
 * Import / open project: file bank is base; keep non-builtin session kits
 * the file doesn't include (by id). Imported id wins on collision.
 */
export function mergeDrumBanksPreservingUser(
  imported: readonly NamedDrumKit[] | undefined,
  existing: readonly NamedDrumKit[] | undefined,
): NamedDrumKit[] {
  const base = sanitizeDrumBank(imported ?? createDefaultDrumBank());
  if (!existing?.length) {
    return base;
  }
  const ids = new Set(base.map(entry => entry.id));
  const out = [...base];
  for (const entry of existing) {
    if (entry.builtin || ids.has(entry.id)) {
      continue;
    }
    out.push({
      id: entry.id,
      name: entry.name,
      kit: cloneDrumKit(entry.kit),
      builtin: false,
    });
    ids.add(entry.id);
  }
  return out;
}
